import {useBynder} from '@/hooks/useBynder'
import {BynderAsset} from '@/services/bynderAssetService'
import {Spinner} from '@/shared-components/Spinner'
import {useQuery} from '@tanstack/react-query'
import {Button, Link} from 'pepsico-ds'
import {useState} from 'react'
import './ImageGalleryModal.scss'

interface ImageGalleryModalProps {
  isOpen: boolean
  onClose: () => void
  onSelectImage: (asset: BynderAsset) => void
  tag?: string
}

export const ImageGalleryModal = ({isOpen, onClose, onSelectImage, tag}: ImageGalleryModalProps) => {
  const [selectedImage, setSelectedImage] = useState<BynderAsset | null>(null)
  const {getAssetsByTag} = useBynder()

  // Fetch gallery assets via Bynder SDK directly
  const {
    data: assetsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['bynder-assets', tag],
    queryFn: () => getAssetsByTag(tag || ''),
    enabled: isOpen && !!tag,
  })

  const handleThumbnailClick = (asset: BynderAsset) => {
    setSelectedImage(selectedImage?.id === asset.id ? null : asset)
  }

  const handleConfirmSelection = () => {
    if (selectedImage) {
      onSelectImage(selectedImage)
      setSelectedImage(null)
    }
  }

  const handleCancel = () => {
    setSelectedImage(null)
    onClose()
  }

  if (!isOpen) {
    return null
  }

  const galleryImages = assetsData?.media || []
  const isNoResults = !galleryImages.length

  return (
    <div className="image-gallery-modal-overlay" data-testid="image-gallery-modal">
      <div className="image-gallery-modal">
        <h3 className="image-gallery-modal__title">Image Gallery</h3>

        <div className="image-gallery-modal__content">
          {error && (
            <div className="image-gallery-modal__error" data-testid="gallery-error">
              Error loading images! Please try again later.
            </div>
          )}

          {isLoading ? (
            <div className="image-gallery-modal__loader" data-testid="gallery-loading">
              <Spinner />
            </div>
          ) : (
            <>
              {isNoResults && (
                <div className="image-gallery-modal__empty" data-testid="gallery-empty">
                  No images found
                </div>
              )}

              {!isNoResults && (
                <div className="image-gallery-modal__grid" data-testid="gallery-success">
                  {galleryImages.map((asset: BynderAsset) => {
                    const isImageSelected = selectedImage?.id === asset.id

                    return (
                      <div
                        key={asset.id}
                        className={`image-gallery-modal__thumbnail ${isImageSelected ? 'selected' : ''}`}
                        data-testid="gallery-thumbnail"
                        onClick={() => handleThumbnailClick(asset)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleThumbnailClick(asset)
                          }
                        }}
                      >
                        <img
                          className="image-gallery-modal__image"
                          src={
                            asset.thumbnails?.thul ||
                            asset.thumbnails?.webimage ||
                            asset.thumbnails?.mini ||
                            asset.original
                          }
                          alt={asset.property_Product_Description ?? asset.name}
                        />
                        <small
                          className="image-gallery-modal__label"
                          title={asset.property_Product_Description ?? asset.name}
                        >
                          {asset.property_Product_Description ?? asset.name}
                        </small>
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>

        <div className="image-gallery-modal__actions">
          <Link data-testid="btn-cancel-gallery" className="btn-cancel-gallery" size="medium" onClick={handleCancel}>
            Cancel
          </Link>
          <Button
            data-testid="btn-confirm-selection"
            className="btn-confirm-selection"
            size="medium"
            onClick={handleConfirmSelection}
            disabled={!selectedImage}
          >
            Confirm selected image
          </Button>
        </div>
      </div>
    </div>
  )
}
